// Excel export for the forecast page. ExcelJS is loaded only when the
// button is pressed, so it never weighs on the dashboard itself.
// Layout follows Ali's spreadsheet style: plain, black text, grey header.
import {
  BELOW, BEDS, COST_OF_SALES, EXCEPTIONAL, fyName, isVariable,
} from "../tabs/Forecast.jsx";

const GBP = '£#,##0;-£#,##0';
const PCT = '+0.0%;-0.0%;0.0%';
const GREY = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEEEEEE" } };

export async function exportXlsx({ base, b0, f, sc, y, fee0, feeStart }) {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  const last = `FY ${fyName(y)}`;

  /* ── Budget ── */
  const ws = wb.addWorksheet("Budget", { views: [{ state: "frozen", ySplit: 1 }] });
  ws.columns = [
    { header: "Line", width: 34 },
    { header: last, width: 15 },
    { header: "Change", width: 14 },
    { header: "Budget", width: 15 },
    { header: "Per bed", width: 12 },
  ];
  const head = ws.getRow(1);
  head.font = { bold: true };
  head.eachCell((c) => { c.fill = GREY; });

  const add = (label, a, b, change, bold) => {
    const r = ws.addRow([label, a, change ?? null, b, b / BEDS]);
    [2, 4, 5].forEach((i) => { r.getCell(i).numFmt = GBP; });
    if (typeof change === "number") r.getCell(3).numFmt = PCT;
    if (bold) r.font = { bold: true };
  };
  const change = (cat, amt) => {
    if (amt === 0) {
      const perBed = sc.lines[cat + "|bed"] || 0;
      return perBed ? `£${perBed} per bed` : null;
    }
    const b = f.lines[cat];
    return amt ? b / amt - 1 : null;
  };
  const line = (cat) => add(cat, base.lines[cat], f.lines[cat], change(cat, base.lines[cat]));
  const cats = Object.keys(base.lines);

  add("Revenue", b0.revenue, f.revenue, f.revenue / b0.revenue - 1, true);
  ws.addRow([]);
  COST_OF_SALES.filter((c) => c in base.lines).forEach(line);
  add("Total cost of sales", b0.cos, f.cos, null, true);
  add("Gross profit", b0.gp, f.gp, null, true);
  ws.addRow([]);
  cats.filter((c) => !COST_OF_SALES.includes(c)).sort().forEach(line);
  add("Total overheads", b0.opex, f.opex, null, true);
  add("EBITDA", b0.ebitda, f.ebitda, null, true);
  add("Adjusted EBITDA", b0.adj, f.adj, null, true);
  ws.addRow([]);
  add(BELOW, b0.below, f.below, base.below ? f.below / base.below - 1 : null);
  add("Post-tax profit", b0.post, f.post, null, true);

  /* ── Assumptions ── */
  const as = wb.addWorksheet("Assumptions");
  as.columns = [{ header: "Assumption", width: 44 }, { header: "Value", width: 40 }];
  as.getRow(1).font = { bold: true };
  as.getRow(1).eachCell((c) => { c.fill = GREY; });
  const occPct = sc.occ / BEDS;
  [
    ["Base year (management accounts)", `${last} (Oct–Sep)`],
    ["Registered beds", BEDS],
    ["Occupancy (average residents)", +sc.occ.toFixed(2)],
    ["Occupancy %", occPct],
    ["Last year's occupancy (average residents)", +base.occ.toFixed(2)],
    ["Average weekly fee, starting point", Math.round(feeStart)],
    ["Last year's average weekly fee", Math.round(fee0)],
    ["Fee change", sc.fee / 100],
    ["Average weekly fee, budget", Math.round(feeStart * (1 + sc.fee / 100))],
    ["General cost inflation (non-wage costs)", sc.infl / 100],
    ["Wage change (NI and pension follow it)", (sc.lines["Direct Wages"] || 0) / 100],
    ["Lines that move with occupancy",
      cats.filter((c) => isVariable(sc, c)).join(", ") || "None"],
    ["Added back for Adjusted EBITDA", EXCEPTIONAL.join(", ")],
    ["Exported", new Date().toLocaleString("en-GB")],
  ].forEach(([k, v]) => {
    const r = as.addRow([k, v]);
    const c = r.getCell(2);
    c.alignment = { horizontal: "left", wrapText: true };
    if (/%|change|inflation/i.test(k)) c.numFmt = '0.0%';
    if (/fee,|weekly fee$/i.test(k)) c.numFmt = GBP;
  });

  const buf = await wb.xlsx.writeBuffer();
  const url = URL.createObjectURL(new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `Aria budget from FY ${fyName(y)} - ${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
